export type ToolResult<T> =
  | {
      ok: true;
      data: T;
    }
  | {
      ok: false;
      error: string;
    };

function toErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}

export function toolResultOk<T>(data: T): ToolResult<T> {
  return { ok: true, data };
}

export function toolResultError(
  error: unknown,
  fallback = "Unknown tool execution error."
): ToolResult<never> {
  return {
    ok: false,
    error: toErrorMessage(error, fallback)
  };
}

export function isToolResult(value: unknown): value is ToolResult<unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const candidate = value as { ok?: unknown; data?: unknown; error?: unknown };
  if (candidate.ok === true) {
    return "data" in candidate;
  }

  if (candidate.ok === false) {
    return typeof candidate.error === "string";
  }

  return false;
}

export function isToolResultOk<T = unknown>(
  value: unknown
): value is { ok: true; data: T } {
  return (
    isToolResult(value) &&
    (value as { ok?: unknown }).ok === true
  );
}

export function getToolResultError(value: unknown): string | undefined {
  if (!isToolResult(value)) {
    return undefined;
  }

  return value.ok ? undefined : value.error;
}

export function getToolResultData<T = unknown>(value: unknown): T | undefined {
  if (!isToolResultOk<T>(value)) {
    return undefined;
  }

  return value.data;
}

